import React, { useEffect, useState } from "react";
import { Input, Modal, notification, Spin } from "antd";
import { DeleteOutlined, GlobalOutlined, LoadingOutlined } from "@ant-design/icons";

import AppHeader from "@/components/header/AppHeader";
import CardContent from "@/components/card/CardContent";
import ModalContentRemove from "@/components/modals/ModalContentRemove";
import { getResponseErrorMessage } from "@/utils/api-service";
import api from "@/api/index";

import "./styles/studio-domains.scss";

function StudioDomains() {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [domains, setDomains] = useState<Array<{ id: number; domain: string }>>([]);
  const [newDomain, setNewDomain] = useState<string>("");
  const [isAdding, setIsAdding] = useState<boolean>(false);
  const [deleteTarget, setDeleteTarget] = useState<{ id: number; domain: string } | null>(null);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  const fetchDomains = async () => {
    try {
      setIsLoading(true);
      const response = await api.studio.getDomains();
      setDomains(response.data.data || []);
    } catch (err: any) {
      notification.error({
        message: "Ошибка загрузки",
        description: getResponseErrorMessage(err, "Не удалось загрузить список доменов"),
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchDomains();
  }, []);

  const isValidDomain = (value: string): boolean => {
    const pattern = /^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}$/i;
    return pattern.test(value.trim());
  };

  const handleAdd = async () => {
    const trimmed = newDomain.trim();

    if (!trimmed) {
      return;
    }

    if (!isValidDomain(trimmed)) {
      notification.warning({
        message: "Некорректный домен",
        description: "Введите домен в формате example.com",
      });
      return;
    }

    if (domains.some((d) => d.domain.toLowerCase() === trimmed.toLowerCase())) {
      notification.warning({
        message: "Домен уже добавлен",
        description: `Домен ${trimmed} уже есть в списке`,
      });
      return;
    }

    try {
      setIsAdding(true);
      await api.studio.addDomain(trimmed);
      notification.success({
        message: "Домен добавлен",
        description: `${trimmed} успешно добавлен в список`,
      });
      setNewDomain("");
      fetchDomains();
    } catch (err: any) {
      notification.error({
        message: "Ошибка",
        description: getResponseErrorMessage(err, "Не удалось добавить домен"),
      });
    } finally {
      setIsAdding(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;

    try {
      setIsDeleting(true);
      await api.studio.removeDomain(deleteTarget.id);
      notification.success({
        message: "Домен удалён",
        description: `${deleteTarget.domain} удалён из списка`,
      });
      setDeleteTarget(null);
      fetchDomains();
    } catch (err: any) {
      notification.error({
        message: "Ошибка",
        description: getResponseErrorMessage(err, "Не удалось удалить домен"),
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (!value.includes("http")) {
      setNewDomain(value.replace(/\s/g, ""));
    }
  };

  if (isLoading) {
    return (
      <div className="studio-domains app-page">
        <AppHeader title="Домены студии" link="/" />
        <div className="app-block-loader">
          <Spin size="large" indicator={<LoadingOutlined />} />
        </div>
      </div>
    );
  }

  return (
    <div className="studio-domains app-page">
      <AppHeader title="Домены студии" link="/" />

      <div className="app-container">
        <div className="app-content">
          <CardContent
            title={
              <>
                Добавить домен
                {domains.length > 0 && (
                  <span className="studio-domains__count">
                    — уже добавлено: {domains.length}
                  </span>
                )}
              </>
            }
          >
            <div className="studio-domains__form">
              <div className="studio-domains__input-group">
                <div className="form-group">
                  <label className="form-group__label">
                    Домен <span className="app-required">*</span>
                  </label>
                  <Input
                    placeholder="studio-site.ru"
                    value={newDomain}
                    onChange={handleInputChange}
                    onPressEnter={handleAdd}
                    disabled={isAdding}
                    prefix={<GlobalOutlined style={{ color: "#bfbfbf" }} />}
                    autoComplete="off"
                  />
                  <span className="studio-domains__input-hint">
                    Например: studio-site.ru, video.mystudio.com
                  </span>
                </div>
              </div>
              <button
                className="app-btn primary"
                onClick={handleAdd}
                disabled={isAdding || !newDomain.trim()}
              >
                {isAdding ? "Добавление..." : "Добавить"}
              </button>
            </div>
          </CardContent>

          <CardContent title={`Домены студии (${domains.length})`}>
            {domains.length === 0 ? (
              <div className="studio-domains__empty">
                <div className="studio-domains__empty-icon">
                  <GlobalOutlined />
                </div>
                <div className="studio-domains__empty-text">
                  Нет добавленных доменов
                </div>
                <div className="studio-domains__empty-hint">
                  Добавьте домен вашего сайта, чтобы использовать эксклюзивный показ озвучек
                </div>
              </div>
            ) : (
              <div className="studio-domains__list">
                {domains.map((item) => (
                  <div key={item.id} className="studio-domains__item">
                    <span className="studio-domains__item-domain">
                      {item.domain}
                    </span>
                    <div className="studio-domains__item-actions">
                      <button
                        className="app-btn-icon delete"
                        onClick={() => setDeleteTarget(item)}
                        title="Удалить домен"
                      >
                        <DeleteOutlined />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </div>
      </div>

      <Modal
        title="Удаление домена"
        open={deleteTarget !== null}
        onOk={handleDeleteConfirm}
        onCancel={() => setDeleteTarget(null)}
        okText="Удалить"
        cancelText="Отмена"
        confirmLoading={isDeleting}
        okButtonProps={{ danger: true }}
      >
        <ModalContentRemove
          text={`Вы уверены, что хотите удалить домен «${deleteTarget?.domain}»?`}
        />
      </Modal>
    </div>
  );
}

export default StudioDomains;
